import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  NotificationContentCategory,
  NotificationType,
} from '@/features/notifications/notification-types';
import {
  getNotificationContentCategoryString,
  getNotificationTypeString,
} from '@/features/notifications/util/notification-util';
import { useOrgMembersQuery } from '@/features/org/hooks/use-org-members-query';
import { defaultOrgName } from '@/features/org/utils/default-org-name';
import { getOrgRoleDisplay } from '@/features/org/utils/type-utils';
import {
  OrgRole,
  orgRoleOptions,
} from '@/features/org/validation/org-role.schema';
import { usePartners } from '@/features/partners/hooks/use-partners';
import {
  ProjectHubEntryFilterType,
  projectHubEntryFilterTypeOptions,
} from '@/features/projects/projects-hub/components/project-hub-filter/project-hub-entry-filter-type';
import {
  getProjectSubprojectsQueryKey,
  useProjectSubprojects,
} from '@/features/projects/shared/hooks/use-project-subprojects';
import { useProjects } from '@/features/projects/shared/hooks/use-projects';
import {
  ProjectRole,
  projectRoleOptions,
} from '@/features/projects/shared/types/project-role';
import {
  getProjectStatusDisplay,
  ProjectStatus,
  projectStatusOptions,
} from '@/features/projects/shared/types/project-status';
import { Filter } from '@/shared/components/filter/filter';
import { FilterSettings } from '@/shared/components/filter/filter-types';
import {
  DocumentVisibility,
  useVisbilityFromString,
  VisbilityOptions,
} from '@/shared/components/ui/upload/document-upload-dialog';
import { HubFilterState } from '@/shared/hooks/use-hub-filter-store';
import { InvitationStatus } from '@/shared/types/api';
import {
  NotificationReadStatus,
  notificationContentCategoryOptions,
  notificationFilterStatusOptions,
  notificationTypeOptions,
} from '@/shared/types/filter/notification-filter-type';
import {
  getInvitationStatusText,
  invitationStatusOptions,
} from '@/shared/utils/invitation-status-helper';
import { getArrayNoDuplicates } from '@/shared/utils/set-utils';

export enum HubFilterOption {
  STATUS = 'status',
  INVITATION_STATUS = 'invitation_status',
  USER_ROLE = 'user_role',
  PARTNER = 'partner',
  LAST_ENTRY = 'last_entry',
  FILTER_RANGE = 'filter_range',
  ROLE = 'role',
  VISIBILITY = 'visibility',
  PROJECT = 'project',
  SUBPROJECT = 'subproject',
  UPLOADED_BY = 'uploaded_by',

  //Notification-hub-specific options
  NOTIFICATION_STATUS = 'notification_status',
  NOTIFICATION_TYPE = 'notification_type',
  NOTIFICATION_CATEGORY = 'notification_category',
}

export type HubFilterOptionSettings = {
  option: HubFilterOption;
  allowMultiple?: boolean;
};

export const HubFilter = (
  props: HubFilterState & {
    activeCount: () => number;
    options?: HubFilterOptionSettings[];
    showPredefinedPeriods?: boolean;
  },
) => {
  const { t } = useTranslation('shared', { keyPrefix: 'Filter' });
  const { t: tInvitationStatus } = useTranslation('shared', {
    keyPrefix: 'InvitationStatus',
  });
  const { translateVisibility } = useVisbilityFromString();

  const { data: partners } = usePartners();
  const { data: projects } = useProjects();
  const { data: users } = useOrgMembersQuery();

  const {
    statuses,
    partnerIds,
    roles,
    lastEntry,
    fromDate,
    toDate,
    invitationStatus,
    userRoles,
    visibility,
    projectIds,
    subprojectIds,
    uploadedBy,
    setFromDate,
    setToDate,
    setStatuses,
    setPartnerIds,
    setRoles,
    setLastEntry,
    setVisibility,
    setProjectIds,
    setSubprojectIds,
    setUploadedBy,
    setInvitationStatus,
    setUserRoles,
    reset,
    activeCount,

    // Notification-hub-specific props
    notificationStatuses,
    notificationTypes,
    notificationCategories,
    setNotificationStatuses,
    setNotificationTypes,
    setNotificationCategories,

    options,
    showPredefinedPeriods = false,
  } = props;

  // Stage selections locally; only commit on Apply
  const [draftStatus, setDraftStatus] = useState<ProjectStatus[]>(
    Array.from(statuses ?? []),
  );
  const [draftPartnerIds, setDraftPartnerIds] = useState<string[]>(
    Array.from(partnerIds ?? []),
  );
  const [draftRoles, setDraftRoles] = useState<ProjectRole[]>(
    Array.from(roles ?? []),
  );
  const [draftNotificationStatuses, setDraftNotificationStatuses] = useState<
    NotificationReadStatus[]
  >(Array.from(notificationStatuses ?? []));
  const [draftLastEntry, setDraftLastEntry] = useState<
    ProjectHubEntryFilterType[]
  >(Array.from(lastEntry ?? []));
  const [draftVisibility, setDraftVisibility] = useState<DocumentVisibility[]>(
    Array.from(visibility ?? []),
  );
  const [draftProjectIds, setDraftProjectIds] = useState<string[]>(
    Array.from(projectIds ?? []),
  );
  const [draftSubprojectIds, setDraftSubprojectIds] = useState<string[]>(
    Array.from(subprojectIds ?? []),
  );
  const [draftUploadedBy, setDraftUploadedBy] = useState<string[]>(
    Array.from(uploadedBy ?? []),
  );
  const [draftFromDate, setDraftFromDate] = useState<Date | undefined>(
    fromDate,
  );
  const [draftToDate, setDraftToDate] = useState<Date | undefined>(toDate);
  const [draftInvitationStatus, setDraftInvitationStatus] = useState<
    InvitationStatus[]
  >(Array.from(invitationStatus ?? []));
  const [draftUserRoles, setDraftUserRoles] = useState<OrgRole[]>(
    Array.from(userRoles ?? []),
  );

  const [draftNotificationTypes, setDraftNotificationTypes] = useState<
    NotificationType[]
  >(Array.from(notificationTypes ?? []));
  const [draftNotificationCategories, setDraftNotificationCategories] =
    useState<NotificationContentCategory[]>(
      Array.from(notificationCategories ?? []),
    );
  const projectId = Number(draftProjectIds?.[0]);
  const { data: subprojects } = useProjectSubprojects({
    projectId: projectId,
    queryProps: {
      queryKey: getProjectSubprojectsQueryKey({
        projectId: projectId,
      }),
      enabled: projectId !== undefined,
    },
  });

  const syncDraftsFromStore = () => {
    setDraftStatus(Array.from(statuses ?? []));
    setDraftPartnerIds(Array.from(partnerIds ?? []));
    setDraftRoles(Array.from(roles ?? []));
    setDraftLastEntry(Array.from(lastEntry ?? []));
    setDraftFromDate(fromDate);
    setDraftToDate(toDate);
    setDraftVisibility(Array.from(visibility ?? []));
    setDraftProjectIds(Array.from(projectIds ?? []));
    setDraftSubprojectIds(Array.from(subprojectIds ?? []));
    setDraftUploadedBy(Array.from(uploadedBy ?? []));
    setDraftInvitationStatus(Array.from(invitationStatus ?? []));
    setDraftUserRoles(Array.from(userRoles ?? []));
    setDraftNotificationStatuses(Array.from(notificationStatuses ?? []));
    setDraftNotificationTypes(Array.from(notificationTypes ?? []));
    setDraftNotificationCategories(Array.from(notificationCategories ?? []));
  };

  useEffect(() => {
    syncDraftsFromStore();
  }, [
    statuses,
    partnerIds,
    roles,
    notificationStatuses,
    lastEntry,
    fromDate,
    toDate,
    visibility,
    projectIds,
    subprojectIds,
    uploadedBy,
    invitationStatus,
    userRoles,
    notificationTypes,
    notificationCategories,
  ]);

  // Clean up stale draft values when available options change
  useEffect(() => {
    if (projects) {
      const validIds = new Set(projects.map((p) => p.id.toString()));
      setDraftProjectIds((prev) => prev.filter((id) => validIds.has(id)));
    }
  }, [projects]);

  useEffect(() => {
    if (partners) {
      const validIds = new Set(partners.map((p) => p.id));
      setDraftPartnerIds((prev) => prev.filter((id) => validIds.has(id)));
    }
  }, [partners]);

  const defaultOptionsOrder: HubFilterOptionSettings[] = [
    HubFilterOption.VISIBILITY,
    HubFilterOption.PROJECT,
    HubFilterOption.STATUS,
    HubFilterOption.PARTNER,
    HubFilterOption.ROLE,
    HubFilterOption.UPLOADED_BY,
    HubFilterOption.NOTIFICATION_STATUS,
    HubFilterOption.LAST_ENTRY,
    HubFilterOption.FILTER_RANGE,
  ].map((option) => ({
    option: option,
  }));

  const allFiltersByOption: Record<HubFilterOption, FilterSettings> = {
    [HubFilterOption.STATUS]: {
      label: t('status'),
      placeholder: t('status'),
      values: (draftStatus as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftStatus((prev) =>
          getArrayNoDuplicates([...prev, value as ProjectStatus]),
        ),
      onRemove: (value: string) =>
        setDraftStatus((prev) =>
          prev.filter((v) => v !== (value as ProjectStatus)),
        ),
      options: projectStatusOptions.map((status) => ({
        label: getProjectStatusDisplay({ status, t }),
        value: status as unknown as string,
      })),
      onClear: () => setDraftStatus([]),
    },
    [HubFilterOption.INVITATION_STATUS]: {
      label: t('invitation_status'),
      placeholder: t('invitation_status'),
      values: (draftInvitationStatus as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftInvitationStatus((prev) =>
          getArrayNoDuplicates([...prev, value as InvitationStatus]),
        ),
      onRemove: (value: string) =>
        setDraftInvitationStatus((prev) => prev.filter((v) => v !== value)),
      options: invitationStatusOptions.map((status) => ({
        label: getInvitationStatusText({ status, t: tInvitationStatus }),
        value: status as unknown as string,
      })),
      onClear: () => setDraftInvitationStatus([]),
    },
    [HubFilterOption.PARTNER]: {
      label: t('partner'),
      placeholder: t('partner'),
      values: (draftPartnerIds as unknown as string[]) ?? [],
      onSelect: (value: string) =>
        setDraftPartnerIds((prev) => getArrayNoDuplicates([...prev, value])),
      onRemove: (value: string) =>
        setDraftPartnerIds((prev) => prev.filter((v) => v !== value)),
      type: 'multiple',
      options: partners?.map((partner) => ({
        label: partner.organization?.name ?? defaultOrgName,
        subLabel:
          partner.alias && partner.alias.trim() !== ''
            ? partner.alias
            : undefined,
        value: partner.id,
      })),
      onClear: () => setDraftPartnerIds([]),
    },
    [HubFilterOption.ROLE]: {
      label: t('role'),
      placeholder: t('role'),
      values: (draftRoles as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftRoles((prev) =>
          getArrayNoDuplicates([...prev, value as ProjectRole]),
        ),
      onRemove: (value: string) =>
        setDraftRoles((prev) => prev.filter((v) => v !== value)),
      options: projectRoleOptions.map((role) => ({
        label: t(`UserType.${role}`),
        value: role as unknown as string,
      })),
      onClear: () => setDraftRoles([]),
    },
    [HubFilterOption.NOTIFICATION_STATUS]: {
      label: t('Notifications.status.title'),
      placeholder: t('Notifications.status.title'),
      values: (draftNotificationStatuses as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftNotificationStatuses((prev) =>
          getArrayNoDuplicates([...prev, value as NotificationReadStatus]),
        ),
      onRemove: (value: string) =>
        setDraftNotificationStatuses((prev) => prev.filter((v) => v !== value)),
      options: notificationFilterStatusOptions.map((notification) => ({
        label: t(`Notifications.status.${notification}`),
        value: notification as unknown as string,
      })),
      onClear: () => setDraftNotificationStatuses([]),
    },
    [HubFilterOption.LAST_ENTRY]: {
      label: t('last_entry'),
      placeholder: t('last_entry'),
      values: (draftLastEntry as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftLastEntry((prev) =>
          getArrayNoDuplicates([...prev, value as ProjectHubEntryFilterType]),
        ),
      onRemove: (value: string) =>
        setDraftLastEntry((prev) => prev.filter((v) => v !== value)),
      options: projectHubEntryFilterTypeOptions.map((lastEntry) => ({
        label: t(`ProjectHubEntryFilterType.${lastEntry}`),
        value: lastEntry as unknown as string,
      })),
      onClear: () => setDraftLastEntry([]),
    },
    [HubFilterOption.FILTER_RANGE]: {
      label: t('filter_range'),
      fromPlaceholder: t('filter_from'),
      toPlaceholder: t('filter_to'),
      from: draftFromDate,
      to: draftToDate,
      onRemoveFrom: () => setDraftFromDate(undefined),
      onRemoveTo: () => setDraftToDate(undefined),
      onSelectFrom: (date) => setDraftFromDate(date),
      onSelectTo: (date) => setDraftToDate(date),
      type: 'dateRange',
    },
    [HubFilterOption.VISIBILITY]: {
      label: t('visibility'),
      placeholder: t('visibility'),
      values: (draftVisibility as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftVisibility((prev) =>
          getArrayNoDuplicates([...prev, value as DocumentVisibility]),
        ),
      onRemove: (value: string) =>
        setDraftVisibility((prev) => prev.filter((v) => v !== value)),
      options: VisbilityOptions.map((visibility) => ({
        label: translateVisibility(visibility as DocumentVisibility),
        value: visibility as unknown as string,
      })),
      onClear: () => setDraftVisibility([]),
    },
    [HubFilterOption.PROJECT]: {
      label: t('project'),
      placeholder: t('project'),
      values: (draftProjectIds as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftProjectIds((prev) => getArrayNoDuplicates([...prev, value])),
      onRemove: (value: string) =>
        setDraftProjectIds((prev) => prev.filter((v) => v !== value)),
      options: projects?.map((project) => ({
        label: project.name,
        value: project.id.toString(),
      })),
      onClear: () => setDraftProjectIds([]),
    },
    [HubFilterOption.SUBPROJECT]: {
      label: t('subproject'),
      placeholder: t('subproject'),
      values: (draftSubprojectIds as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftSubprojectIds((prev) => getArrayNoDuplicates([...prev, value])),
      onRemove: (value: string) =>
        setDraftSubprojectIds((prev) => prev.filter((v) => v !== value)),
      options:
        subprojects?.map((subproject) => ({
          label: subproject.name,
          value: subproject.id.toString(),
        })) ?? [],
      onClear: () => setDraftSubprojectIds([]),
    },
    [HubFilterOption.UPLOADED_BY]: {
      label: t('uploaded_by'),
      placeholder: t('uploaded_by'),
      values: (draftUploadedBy as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftUploadedBy((prev) => getArrayNoDuplicates([...prev, value])),
      onRemove: (value: string) =>
        setDraftUploadedBy((prev) => prev.filter((v) => v !== value)),
      options: users?.map((user) => ({
        label: user.profile.name
          ? `${user.profile.name} (${user.profile.email})`
          : (user.profile.email ?? 'Keine Angabe'),
        value: user.user_id,
      })),
      onClear: () => setDraftUploadedBy([]),
    },
    [HubFilterOption.USER_ROLE]: {
      label: t('user_role'),
      placeholder: t('user_role'),
      values: (draftUserRoles as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftUserRoles((prev) =>
          getArrayNoDuplicates([...prev, value as OrgRole]),
        ),
      onRemove: (value: string) =>
        setDraftUserRoles((prev) => prev.filter((v) => v !== value)),
      options: orgRoleOptions.map((role) => ({
        label: getOrgRoleDisplay(
          role,
          useTranslation('org', { keyPrefix: 'Roles' }).t as (
            key: string,
          ) => string,
        ),
        value: role as unknown as string,
      })),
      onClear: () => setDraftUserRoles([]),
    },
    [HubFilterOption.NOTIFICATION_TYPE]: {
      label: t('Notifications.type'),
      placeholder: t('Notifications.type'),
      values: (draftNotificationTypes as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftNotificationTypes((prev) =>
          getArrayNoDuplicates([...prev, value as NotificationType]),
        ),
      onRemove: (value: string) =>
        setDraftNotificationTypes((prev) => prev.filter((v) => v !== value)),
      options: notificationTypeOptions.map((notificationType) => ({
        label: getNotificationTypeString(notificationType),
        value: notificationType as unknown as string,
      })),
      onClear: () => setDraftNotificationTypes([]),
    },
    [HubFilterOption.NOTIFICATION_CATEGORY]: {
      label: t('Notifications.category'),
      placeholder: t('Notifications.category'),
      values: (draftNotificationCategories as unknown as string[]) ?? [],
      type: 'multiple',
      onSelect: (value: string) =>
        setDraftNotificationCategories((prev) =>
          getArrayNoDuplicates([...prev, value as NotificationContentCategory]),
        ),
      onRemove: (value: string) =>
        setDraftNotificationCategories((prev) =>
          prev.filter((v) => v !== value),
        ),
      options: notificationContentCategoryOptions.map((category) => ({
        label: getNotificationContentCategoryString(category),
        value: category as unknown as string,
      })),
      onClear: () => setDraftNotificationCategories([]),
    },
  };

  const selectedOptions = options == null ? defaultOptionsOrder : options;
  const filters: FilterSettings[] = selectedOptions
    .map((opt) => {
      const filter = allFiltersByOption[opt.option];
      if (opt.allowMultiple === false && filter.type === 'multiple') {
        const { values, onSelect, ...rest } = filter;
        return {
          ...rest,
          type: 'single' as const,
          value: values?.[0],
          onSelect: (value: string | undefined) => {
            filter.onClear?.();
            if (value) onSelect?.(value);
          },
        };
      }
      return filter;
    })
    .filter(Boolean);

  return (
    <Filter
      activeCount={activeCount()}
      onOpenChange={syncDraftsFromStore}
      onReset={() => {
        reset();
      }}
      onApply={() => {
        setStatuses(draftStatus);
        setRoles(draftRoles);
        setPartnerIds(draftPartnerIds);
        setLastEntry(draftLastEntry);
        setFromDate(draftFromDate);
        setToDate(draftToDate);
        setVisibility(draftVisibility);
        setProjectIds(draftProjectIds);
        setSubprojectIds(draftSubprojectIds);
        setUploadedBy(draftUploadedBy);
        setInvitationStatus(draftInvitationStatus);
        setUserRoles(draftUserRoles);
        setNotificationStatuses(draftNotificationStatuses);
        setNotificationTypes(draftNotificationTypes);
        setNotificationCategories(draftNotificationCategories);
      }}
      filters={filters}
      showPredefinedPeriods={showPredefinedPeriods}
    />
  );
};
